import { buildPresenceRow } from '~/utils/sessionSecurity'

// Module-scope mutex: prevents concurrent refreshSession() calls when multiple
// 401s arrive simultaneously (e.g. heartbeat + polling requests all expiring at once).
let isRefreshing = false

export default defineNuxtPlugin(() => {
  if (import.meta.server) return

  // Capture all composables at setup time (Nuxt context). The callbacks below
  // only read .value from these — never call a composable inside setInterval /
  // onAuthStateChange (those run outside Nuxt context and would throw).
  const supabase  = useSupabaseClient()
  const user      = useSupabaseUser()
  const router    = useRouter()
  const authStore = useAuthStore()

  // Attempt a silent token refresh. On failure, Supabase fires SIGNED_OUT which
  // @nuxtjs/supabase redirects to /auth/login (stripping any query params).
  // We set a sessionStorage flag BEFORE the refresh attempt so the login page
  // can show the "session expired" toast regardless of who wins the redirect race.
  async function handleExpiredSession() {
    if (isRefreshing) return
    if (!navigator.onLine) return  // offline — skip, don't redirect
    isRefreshing = true
    try {
      sessionStorage.setItem('auth_redirect_reason', 'session_expired')
      await supabase.auth.refreshSession()
      // On success → TOKEN_REFRESHED fires → sendHeartbeat() runs automatically.
      // Clear the flag — session is alive, no need to show the toast.
      sessionStorage.removeItem('auth_redirect_reason')
    } finally {
      isRefreshing = false
    }
  }

  async function sendHeartbeat() {
    if (!user.value) return
    const meta = (user.value.app_metadata ?? {}) as Record<string, any>
    const impersonatedClinicId = meta.impersonated_clinic_id ?? null
    // Mirror useUXAnalytics/auth.ts: impersonated clinic takes precedence.
    const activeClinicId = (impersonatedClinicId ?? authStore.clinicId ?? null) as string | null

    const row = buildPresenceRow({
      userId:         user.value.id,
      clinicId:       activeClinicId,
      // Raw profile role — NOT authStore.role (its getter defaults to 'doctor',
      // which mislabels an admin if the heartbeat fires before the profile loads).
      // null until loaded; the next heartbeat fills the real role.
      role:           ((authStore.profile?.role ?? null)) as string | null,
      route:          router.currentRoute.value.path,
      userAgent:      navigator.userAgent,
      isImpersonated: Boolean(impersonatedClinicId),
      nowIso:         new Date().toISOString(),
    })
    try {
      const { status } = await supabase.from('user_presence').upsert(row, { onConflict: 'user_id' })
      if (status === 401) void handleExpiredSession()
    } catch { /* network error — ignore, next interval will retry */ }
  }

  // Record the login origin. GoTrue's audit log is empty on this deployment, so the
  // server-side IP is captured by the record-auth-event edge fn (it reads the proxy
  // headers the browser cannot forge). Fire-and-forget; never blocks the UI.
  function recordAuthEvent(action: 'login' | 'token_refreshed') {
    supabase.functions.invoke('record-auth-event', { body: { action } }).then(() => {}, () => {})
  }

  // Separate subscription: the existing auth-store-reset plugin deliberately
  // ignores SIGNED_IN/TOKEN_REFRESHED, so we add our own here.
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN') { void sendHeartbeat(); recordAuthEvent('login') }
    else if (event === 'TOKEN_REFRESHED') { void sendHeartbeat(); recordAuthEvent('token_refreshed') }
    else if (event === 'SIGNED_OUT') {
      // Refresh token also dead (or session revoked server-side). Redirect to login
      // unless already there, to avoid a redirect loop.
      const path = router.currentRoute.value.path
      if (!path.startsWith('/auth/login')) {
        void router.replace('/auth/login?reason=session_expired')
      }
    }
  })

  void sendHeartbeat()
  setInterval(() => { void sendHeartbeat() }, 90 * 1000) // every 90s
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void sendHeartbeat()
  })
})
